import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { entityFormatResolved } from '@/app/store/selectedEntitySlice';
import { logger } from '@/core/logging';
import { apiClient } from '@/shared/services/api/client';
import type { ApiEnvelope } from '@/shared/services/api/envelope';

/**
 * Reads the selected entity's effective date and time format, once per entity.
 *
 * <h2>Why the platform is asked rather than the client deciding</h2>
 *
 * <p>An effective format is the entity's own choice, then its organization's, then the platform's default.
 * That chain is resolved on the server — see `LegalEntityResponse` — so no screen has to know it exists
 * and two screens cannot disagree about what a row's dates look like. This hook does nothing but fetch
 * the answer and put it where the renderers can find it.
 *
 * <h2>Mounted once, in the shell</h2>
 *
 * <p>The format belongs to the session's entity, not to any one screen, and every master draws dates with
 * it. Loading it per screen would issue the same read on each navigation and leave the first paint of
 * each one showing unformatted dates.
 *
 * <h2>A failure costs formatting, never a screen</h2>
 *
 * <p>Nothing here can stop a page rendering. Where the read fails the store keeps no format, and dates
 * fall back to the platform's own spelling — readable, and visibly not a choice anybody made.
 */

/** Only the fields the renderers need; the rest of the entity is somebody else's business. */
interface EntityFormatDto {
  datePattern?: string | null;
  timePattern?: string | null;
  timeZoneIdentifier?: string | null;
  platformZoneIdentifier?: string | null;
}

export function useEntityDisplayFormat(): void {
  const dispatch = useAppDispatch();
  const entityId = useAppSelector((state) => state.selectedEntity.id);
  /* Bumped when the entity's own settings are saved -- see `entityFormatStale`. */
  const version = useAppSelector((state) => state.selectedEntity.formatVersion);
  /*
    Which (entity, version) the last read was for.

    One key covers both reasons to read: a different entity is selected, or this entity's settings have
    just been saved. Anything else -- a re-render, another screen mounting -- leaves the key unchanged and
    issues nothing.
  */
  const asked = useRef<string | null>(null);

  useEffect(() => {
    if (entityId === null) return;
    const wanted = `${String(entityId)}:${String(version)}`;
    if (asked.current === wanted) return;
    asked.current = wanted;

    void (async () => {
      try {
        const response = await apiClient.get<ApiEnvelope<EntityFormatDto>>(
          `/legal-entities/${String(entityId)}`,
          /* An entity this caller may not read is not a reason to fail the shell. */
          { _allowForbidden: true },
        );
        const entity = response.data.data;
        dispatch(
          entityFormatResolved({
            entityId,
            format: {
              datePattern: entity.datePattern ?? null,
              timePattern: entity.timePattern ?? null,
              zone: entity.timeZoneIdentifier ?? null,
              storedIn: entity.platformZoneIdentifier ?? null,
            },
          }),
        );
      } catch {
        logger.warn('The entity display format could not be read; dates will use the platform default');
        /* Cleared so a later render may try again rather than the session going without a format. */
        asked.current = null;
      }
    })();
  }, [dispatch, entityId, version]);
}
